import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';

import { CimaCore, Util, XmStore, XmStoreUtil } from 'core/services';
import { Subscription } from 'rxjs';
import cloneDeep from 'lodash-es/cloneDeep';
import { CmsCore, CmsDataDetail } from 'store/cms/models';
import { User } from 'store/user/models';
import { LineSummary, PlanSummary } from 'store/account-summary/models';
import { BroadbandLabels } from 'store/broadband-label/models';
import { CONFIG_TOKEN, retiredPlanBannerPlanIds } from 'core/constants';
import { IXMOptions } from 'core/interfaces';
@Component({
    selector: 'cbm-broadband-label',
    styleUrls: ['./cbm-broadband-label.scss'],
    templateUrl: './cbm-broadband-label.html',
    standalone: false
})
export class CbmBroadbandLabelComponent implements OnInit, OnDestroy {
    @Input() public currentLine: LineSummary;
    @Input() public currentPlan: PlanSummary;
    @Input() public isGrandFatheredLabel: boolean;
    public pageData: CmsDataDetail;
    public labelDetails: CBMLabelSegmentDetails;
    public activePlanId: string;
    public activeTabPlanId: string;
    public broadbandLabelsResponse: BroadbandLabels;
    public broadbandLabels: NutritionLabel[] = [];
    public labelResponse: NutritionLabel[] = [];
    public inUseIcon: MediaImageOptions;
    public cbmLabelContent: CBMLabelDetails;
    public planList: LabelTabDetail[] = [];
    public availableTabs: LabelTabDetail[] = [];
    public infoIconGrey: MediaImageOptions;
    public isSeg5: boolean = false;
    public isCbmNoLevel: boolean = false;

    private xmStore: XmStore;
    private subscriptions: Subscription[] = [];

    constructor(xmStore: XmStore, cimaCore: CimaCore, @Inject(CONFIG_TOKEN) config: IXMOptions) {
        Object.assign(this, { xmStore, cimaCore, config });
    }

    public ngOnInit(): void {
        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<User>(User), (user: User) => {
            this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsDataDetail>(CmsDataDetail), (pageData: CmsDataDetail) => {
                this.isSeg5 = user.account.isSegmentFive;
                this.isCbmNoLevel = user.account.isNoLevel;
                this.pageData = pageData;
                this.cbmLabelContent = pageData.cbmBroadbandLabelContent;
                this.labelDetails = pageData.cbmBroadbandLabel.find((label: CBMLabelSegmentDetails) => 
                    user.account.level === 'LEVEL_5' ? label.level === 'LEVEL_5_Plans_7.0' : label.level === user.account.level);
                this.activePlanId = this.currentPlan.isByTheGig || this.currentPlan.isByTheGigShared ? this.currentPlan.bucketId : this.currentLine.planId;
            }));
        }));

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsCore>(CmsCore), (cms: CmsCore) => {
            this.infoIconGrey = {
                mobile: {
                    base: cms.icons.infoIconBlue
                }
            };
        }));

        if (this.isSeg5 || this.isCbmNoLevel) {
            this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<BroadbandLabels>(BroadbandLabels), ((broadbandLabel: BroadbandLabels) => {
                const broadbandLabelList: NutritionLabel[] = broadbandLabel?.labels;
                const isPremiumPlan: boolean = broadbandLabelList?.some((nutritionLabel: NutritionLabel) => nutritionLabel.planId === this.cbmLabelContent.segmentFiveCBMPremiumPlanId);
                this.broadbandLabels = isPremiumPlan ? broadbandLabelList : this.setPremiumLabel(broadbandLabel?.labels);
                this.planList = this.activePlanId === this.cbmLabelContent.segmentFiveCBMPremiumPlanId
                    ? this.labelDetails?.mobilePlanList_level_5_legacy_premium
                    : this.currentLine.planCode?.includes(this.cbmLabelContent.segmentFiveCBMByTheGigPlanId)
                        ? this.labelDetails?.mobilePlanList_level_5_legacy_byTheGig
                        : this.labelDetails?.mobilePlanList;
                this.planList?.forEach((plan: LabelTabDetail) => {
                    const foundLabel: NutritionLabel = this.broadbandLabels?.find((nutritionLabel: NutritionLabel) => nutritionLabel.planId === plan.planId);
                    if (foundLabel) {
                        this.labelResponse.push(foundLabel);
                        this.availableTabs.push(plan);
                    }
                });

                if (this.labelResponse?.length > 0) {
                    const isActivePlan: boolean = this.labelResponse.some((plan: NutritionLabel) => plan.planId === this.activePlanId);
                    this.activeTabPlanId = isActivePlan ? this.activePlanId : this.labelResponse[0].planId;
                }
            })));

        }
    }

    public tabChange(planId: string): void {
        this.activeTabPlanId = planId;
    }

    public showretiredBanner(): boolean {
        return retiredPlanBannerPlanIds.includes(this.currentLine.planId) && this.isSeg5;
    }

    public setPremiumLabel(broadbandLabelsResponse: NutritionLabel[]): NutritionLabel[] {
        if (broadbandLabelsResponse) {
            const getPremiumFlexLabel: NutritionLabel = broadbandLabelsResponse?.find((nutritionLabel: NutritionLabel) => nutritionLabel.planId === this.cbmLabelContent.segmentFiveCBMPremiumFlexPlanId);
            const premiumFlexLabel: NutritionLabel = cloneDeep(getPremiumFlexLabel);
            const premiumLabel: NutritionLabel = { 
                ...premiumFlexLabel, 
                planId: this.cbmLabelContent.segmentFiveCBMPremiumPlanId,
                tierTitle: this.cbmLabelContent.segmentFivePremiumPlanDetails.segmentFiveCBMPremiumTierTitle,
                labelId: this.cbmLabelContent.segmentFivePremiumPlanDetails.labelId
            };

            return this.updatePremiumDisclaimer([...broadbandLabelsResponse, premiumLabel]);
        }

        return broadbandLabelsResponse;
    }

    public updatePremiumDisclaimer(labelResponse: NutritionLabel[]): NutritionLabel[] {
        const premiumLabel: NutritionLabel = labelResponse?.find((nutritionLabel: NutritionLabel) => nutritionLabel.planId === this.cbmLabelContent.segmentFiveCBMPremiumPlanId);
        premiumLabel?.regions?.forEach((region: NutritionLabelRegion) => {
            if (region?.sections) {
                region.sections?.forEach((section: NutritionLabelSection) => {
                    if (section?.disclaimer) {
                        section.disclaimer = section.disclaimer?.map((disclaimer: string) => disclaimer?.includes(this.cbmLabelContent.segmentFivePremiumPlanDetails.segmentFiveCBMPremiumFlexDisclaimer)
                            ? this.cbmLabelContent.segmentFivePremiumPlanDetails.segmentFiveCBMPremiumDisclaimer
                            : disclaimer);
                    }
                });
            }
        });

        return labelResponse;
    }

    public ngOnDestroy(): void {
        Util.unsubscribeAll(this.subscriptions);
    }
}
